import {cloneDeep, get} from "lodash";
import {getArray, getBool, getNumber, getString, isset} from "Core/helpers/data";
import {PatientStudyInfoDataObject, PatientStudyInfoManualDateDataObject} from "./dataObjects";
import {getDate, getDateString, STANDARD_DATE_TIME_FORMAT} from "Core/helpers/datetime";
import {GENDER_PATIENT_GENDER_MAP, PATIENT_GENDER_GENDER_MAP} from '../../const';
import {study_night_date_format} from 'Config/app';

/**
 * Get input data from raw data
 * @param {Object} rawData - Raw data received from IO.
 * @return {PatientStudyInfoDataObject}
 */
export const input = rawData => {
	let result = cloneDeep(rawData);

	result = new PatientStudyInfoDataObject(
		getString(result, 'subjectData.medicalRecordId'),
		getString(result, 'subjectId'),
		getString(result, 'subjectData.firstName'),
		getString(result, 'subjectData.middleName'),
		getString(result, 'subjectData.lastName'),
		getDate(get(result, 'subjectData.dateOfBirth', null), STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S),
		get(result, 'subjectData.gender', null) === null ? 
			null : getString(PATIENT_GENDER_GENDER_MAP, getString(result, 'subjectData.gender')),
		getString(result, 'subjectData.patientId'),
		getString(result, 'subjectData.subjectAction'),
		getString(result, 'studyType.id'),
		getString(result, 'night1Memo'),
		getString(result, 'night2Memo'),
		getString(result, 'night3Memo'),
		getString(result, 'orderingPhysicianId'),
		undefined,
		getArray(result, 'studyNightDataList').map(i => new PatientStudyInfoManualDateDataObject(
			getString(i, 'id'),
			getString(i, 'studyId'),
			getString(i, 'savedRecordingDate'),
			getString(i, 'manuallyAddedRecordingDate'),
			getDate(get(i, 'manuallyAddedRecordingDateFormatted', null), study_night_date_format),
			getString(i, 'manuallyAddedRecordingTimeH'),
			getString(i, 'manuallyAddedRecordingTimeM'),
			getNumber(i, 'night')
		)),
		getDate(get(result, 'dateOfBirthPosition', null), STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S),
		getBool(result, 'yearWarning')
	);

	return result;
}

/**
 * Get output data from input data
 * 
 * @param {PatientStudyInfoDataObject} inputData - Input data.
 * @param {string} studyId - ID of the study.
 * @return {Object}
 */
export const output = (inputData, studyId) => {
	let result = cloneDeep(inputData);
	
	result = {
		studyId,
		subjectId: result.patientId,
		subjectData: {
			id: result.patientId,
			firstName: result.patientFirstName,
			lastName: result.patientLastName,
			middleName: result.patientMiddleName,
			dateOfBirth: getDateString(result.patientDateOfBirth, STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S),
			gender: !isset(result.patientGender) || result.patientGender === null ? 
				null : GENDER_PATIENT_GENDER_MAP[result.patientGender],
			patientId: result.patientOptionalId,
			subjectAction: result.patientAction,
		},
		studyType: {
			id: result.studyTypeId,
		},
		orderingPhysicianId: result.physicianId,
		orderingPhysicians: (
			result.physicianId ?
				{
					id: result.physicianId
				}
				:
				{
					id: '',
					firstName: result.newPhysician.firstName,
					lastName: result.newPhysician.lastName,
					credentials: result.newPhysician.credentials
				}
		),
		night1Memo: result.studyNight1,
		night2Memo: result.studyNight2,
		night3Memo: result.studyNight3,
		studyNightDataList: getArray(result.manualDates).map(i => ({
			id: getString(i, 'id'),
			studyId: getString(i, 'studyId'),
			night: getNumber(i, 'ordinal'),
			manuallyAddedRecordingDate: getString(i, 'manualDate'),
			manuallyAddedRecordingDateFormatted: getDateString(i.manualDateFormatted, study_night_date_format),
			manuallyAddedRecordingTimeH: getString(i, 'manualTimeH'),
			manuallyAddedRecordingTimeM: getString(i, 'manualTimeM'),
			savedRecordingDate: getString(i, 'savedDate')
		})),
	};

	return result;
}